import { Component, Input, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { take } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { Subject } from 'rxjs';

import { ApiError, AuthService, ConfigService, MessageService } from '../../../../core';
import { ConfigEmpresa } from '../../../configuracoes/app/empresa';
import { ConfigCliente } from '../../../configuracoes/app/cliente';
import { ClienteAnaliseMercadoClienteListStorage } from './cliente-analise-mercado-cliente-list-storage';
import { ClienteAnaliseMercadoClienteListStorageService } from './cliente-analise-mercado-cliente-list-storage.service';
import { ClienteAnaliseMercadoClienteList } from './cliente-analise-mercado-cliente-list';
import { ClienteAnaliseMercadoFilter } from '../../adapters';
import { ClienteAnaliseMercadoSearchViewCliente } from '../cliente-analise-mercado-search-view-cliente';
import { ClienteAnaliseMercadoSearchCliente } from '../cliente-analise-mercado-search-cliente';
import { ClienteAnaliseMercadoSearchClienteOrderBy, ClienteAnaliseMercadoSearchClienteOrderByColumn as orderByColumn } from '../cliente-analise-mercado-search-cliente-orderby';
import { ClienteAnaliseMercadoClienteHttpService } from '../services';
import { ClienteAnaliseMercadoClienteTableComponent } from '../table';
import { ClienteAnaliseMercadoFormModalComponent } from '../../form/modal';
import { ClienteAnaliseMercadoLancamentoFormModalComponent } from '../../lancamento';
import { getPerspectiva, isPerspectivaRAP, isPerspectivaClassificacao } from '../../perspectivas/cliente-analise-mercado-perspectiva';
import { ClienteFormModalComponent } from '../../../pessoa/cliente/form-modal';
import { Cliente } from '../../../pessoa/cliente';
import { ClienteCarteiraPreBaixarModalComponent } from '../../../cliente-carteira/prebaixar';
import { ClienteCarteiraBaixarModalComponent } from '../../../cliente-carteira/baixar';
import { pessoaCentralCompras } from '../../../pessoa/tipo';
import { ClienteCentralCompra, ClienteCentralCompraParticipante, ClienteCentralCompraFormModalComponent, ClienteCentralCompraParticipanteFormInsertModalComponent } from '../../../pessoa/cliente/central-compra';
import { ClienteAnaliseMercadoClassificacao, ClienteAnaliseMercadoClassificacaoHttpService } from '../../classificacao';
import { GrupoClassificacao, GrupoClassificacaoCrudHttpService } from '../../../grupo-classificacao';
import { CdkScrollable } from '@angular/cdk/scrolling';

interface Config {
    /** Se os lançamentos são controlados por quantidade */
    controleQuantidade: boolean;
    /** Se os lançamentos são controlados por peso */
    controlePeso: boolean;
    /** Se os lançamentos são controlados por valor */
    controleValor: boolean;
    /** Se deve exibir a Classificação RAP do cliente */
    isShowClassificaoRAP: boolean;
    /** Se deve exibir o inidicador Customer Share */
    isShowCustomerShare: boolean;
}

@Component({
    selector: 'sgt-cliente-analise-mercado-cliente-list',
    templateUrl: './cliente-analise-mercado-cliente-list.component.html',
    styleUrls: ['./cliente-analise-mercado-cliente-list.component.less']
})
export class ClienteAnaliseMercadoClienteListComponent implements OnInit, OnDestroy {
    config: Config;
    configEmpresa: ConfigEmpresa;
    configCliente: ConfigCliente;
    isRepresentante: boolean;

    @Input() visualizacaoStorageKey = 'clienteAnaliseMercadoClienteListVisualizacao';
    visualizacaoStorage: ClienteAnaliseMercadoClienteListStorage;

    @Input() filter: ClienteAnaliseMercadoFilter;

    private searchViewSubs$ = new Subscription();
    /** Quantidade limite de clientes que devem ser retornados na primeira pesquisa (Search View) */
    private limitFirstClientes = 200;
    /** Quantidade limite de clientes que devem ser retornados nas pesquisa sob-demanda (Infinite Scroll) */
    private limitClientes = 50;
    private searchClientesSubs$ = new Subscription();
    clientes = new Subject<ClienteAnaliseMercadoSearchCliente[]>();
    list: ClienteAnaliseMercadoClienteList;
    orderBy: ClienteAnaliseMercadoSearchClienteOrderBy;

    /** cliente atual que está sendo analisado */
    private currentCliente: ClienteAnaliseMercadoSearchCliente;

    @ViewChild(CdkScrollable, {static: true}) scrollable: CdkScrollable;
    @ViewChild('table') table: ClienteAnaliseMercadoClienteTableComponent;
    @ViewChild('formModal') formModal: ClienteAnaliseMercadoFormModalComponent;
    @ViewChild('lancamentoFormModal') lancamentoFormModal: ClienteAnaliseMercadoLancamentoFormModalComponent;
    @ViewChild('clienteFormModal') clienteFormModal: ClienteFormModalComponent;
    @ViewChild('clienteCarteiraPreBaixarModal') clienteCarteiraPreBaixarModal: ClienteCarteiraPreBaixarModalComponent;
    @ViewChild('clienteCarteiraBaixarModal') clienteCarteiraBaixarModal: ClienteCarteiraBaixarModalComponent;
    @ViewChild('clienteCentralCompraFormModal') clienteCentralCompraFormModal: ClienteFormModalComponent;
    @ViewChild('centralCompraFormModal') centralCompraFormModal: ClienteCentralCompraFormModalComponent;
    @ViewChild('centralCompraParticipanteFormModal') centralCompraParticipanteFormModal: ClienteCentralCompraParticipanteFormInsertModalComponent;

    constructor(private authService: AuthService,
                private configService: ConfigService,
                private messageService: MessageService,
                private clienteAnaliseMercadoClienteListStorageService: ClienteAnaliseMercadoClienteListStorageService,
                private clienteAnaliseMercadoClienteHttpService: ClienteAnaliseMercadoClienteHttpService,
                private clienteAnaliseMercadoClassificacaoHttpService: ClienteAnaliseMercadoClassificacaoHttpService,
                private grupoClassificacaoCrudHttpService: GrupoClassificacaoCrudHttpService) { }

    ngOnInit(): void {
        this.configEmpresa = this.configService.getEmpresa();
        this.configCliente = this.configService.getCliente();
        this.isRepresentante = this.authService.isRepresentante();
        this.setConfig();
        this.getStorage();
    }

    private setConfig() {
        this.config = {
            controleQuantidade: this.isControleQuantidade(),
            controlePeso: this.isControlePeso(),
            controleValor: this.isControleValor(),
            isShowClassificaoRAP: isPerspectivaRAP(this.filter.visualizacaoPerspectiva),
            isShowCustomerShare: isPerspectivaRAP(this.filter.visualizacaoPerspectiva)
        };
    }

    /** Verificado se deve exibir o controle por quantidade nos lançamentos */
    private isControleQuantidade(): boolean {
        if (isPerspectivaRAP(this.filter.visualizacaoPerspectiva)) {
            return false;
        }

        return this.configEmpresa.controleQuantidade;
    }

    /** Verificado se deve exibir o controle por peso nos lançamentos */
    private isControlePeso(): boolean {
        if (isPerspectivaRAP(this.filter.visualizacaoPerspectiva)) {
            return false;
        }

        return this.configEmpresa.controlePeso;
    }

    /** Verificado se deve exibir o controle por valor nos lançamentos */
    private isControleValor(): boolean {
        if (isPerspectivaRAP(this.filter.visualizacaoPerspectiva)) {
            return true;
        }

        return this.configEmpresa.controleValor;
    }


    /** Obtendo o storage da visualização do list */
    private getStorage() {
        this.visualizacaoStorage = this.clienteAnaliseMercadoClienteListStorageService.get(this.visualizacaoStorageKey);

        // Order By
        if (this.visualizacaoStorage.orderBy) {
            this.setOrderBy(this.visualizacaoStorage.orderBy);
        } else {
            this.setOrderBy({
                column: orderByColumn.NomeCliente,
                dir: 'asc',
                tableOrderBy: [[1, 'asc']]
            });
        }
    };

    /** Realiza a pesquisa das análises de mercados dos clientes */
    async search() {
        try {
            this.clienteAnaliseMercadoClienteListStorageService.set(this.visualizacaoStorageKey, this.visualizacaoStorage);

            this.table.setTabeLoading(true);

            const searchView = await this.searchView();
            this.list = new ClienteAnaliseMercadoClienteList(searchView, this.limitClientes);
            this.clientes.next(this.list.getClientes());

            this.scrollable?.scrollTo({top: 0, behavior: 'smooth'});
            this.table.setTabeLoading(false);
        } catch (error) {
            console.error(error);
            this.table.setTabeLoading(false);
        }
    }

    /** Realiza a pesquisa do search view dos clientes nas análises de mercados */
    private searchView(): Promise<ClienteAnaliseMercadoSearchViewCliente> {
        return new Promise<ClienteAnaliseMercadoSearchViewCliente>((resolve, reject) => {
            this.searchViewSubs$?.unsubscribe();
            this.searchViewSubs$ = this.clienteAnaliseMercadoClienteHttpService.searchView(this.filter, this.orderBy.column, this.orderBy.dir, this.limitFirstClientes)
                .subscribe(
                    searchView => resolve(searchView),
                    (error: ApiError) => {
                        this.messageService.error(error.cause, error.message, error);
                        reject(error);
                    }
                );
        });
    }


    /** Pesquisa os clientes dentro do range da pesquisa sob-demanda */
    private searchClientes() {
        this.searchClientesSubs$?.unsubscribe();

        this.table.setTabeLoading(true);
        this.searchClientesSubs$ = this.clienteAnaliseMercadoClienteHttpService.search(this.list.getMoreClientes(), this.filter, this.orderBy.column, this.orderBy.dir, -1, -1)
            .subscribe(
                clientes => {
                    this.list.addClientes(clientes);
                    this.clientes.next(this.list.getClientes());
                },
                (error: ApiError) => this.messageService.error(error.cause, error.message, error),
                () => this.table.setTabeLoading(false)
            );
    }

    /** Carrega e atualiza a análise de mercado do cliente informado */
    private searchOneCliente(idCliente: number) {
        this.clienteAnaliseMercadoClienteHttpService.searchOne(idCliente, this.filter)
            .pipe(
                take(1)
            )
            .subscribe(
                cliente => {
                    if (cliente) {
                        this.list.updateOneCliente(cliente, this.configCliente);
                    } else { // Se o cliente for null então significa que não deve estar mais presente na listagem
                        this.list.removeOneCliente(idCliente, this.configCliente);
                    }

                    this.clientes.next(this.list.getClientes());
                },
                (error: ApiError) => this.messageService.error(error.cause, error.message, error)
            );
    }

    /** Ao realizar o infinite scroll */
    onInfiniteScroll() {
        if (this.list.done) {
            return;
        }
        this.searchClientes();
    }

    /** Seta o Order By da pesquisa */
    private setOrderBy(orderBy: ClienteAnaliseMercadoSearchClienteOrderBy) {
        this.orderBy = orderBy;
        this.visualizacaoStorage.orderBy = this.orderBy;
    }

    /** Ao alterar o Order By da pesquisa */
    onOrderByChange(orderBy: ClienteAnaliseMercadoSearchClienteOrderBy) {
        this.setOrderBy(orderBy);
        this.search();
    }

    /** Seta o cliente atual que está sendo analisado */
    private setCurrentCliente(cliente: ClienteAnaliseMercadoSearchCliente) {
        this.currentCliente = cliente;
    }

    /** Obtem do lançamento da classificação através do cliente, ano e grupo de classificação informados */
    private findAnaliseMercadoClassificacao(idCliente: number, ano: number, idGrupoClassificacao: number): Promise<ClienteAnaliseMercadoClassificacao> {
        return new Promise<ClienteAnaliseMercadoClassificacao>((resolve, reject) => {
            this.clienteAnaliseMercadoClassificacaoHttpService.findIDByClienteAnoGrupoClassificacao(idCliente, ano, idGrupoClassificacao)
                .pipe(
                    take(1)
                )
                .subscribe(
                    analiseMercadoClassificacao => resolve(analiseMercadoClassificacao),
                    (error: ApiError) => {
                        this.messageService.error(error.cause, error.message, error);
                        reject(error);
                    }
                );
        });
    }

    /** Obtem o grupo de classificação informado */
    private getGrupoClassificacao(idGrupoClassificacao: number): Promise<GrupoClassificacao> {
        return new Promise<GrupoClassificacao>((resolve, reject) => {
            this.grupoClassificacaoCrudHttpService.find(idGrupoClassificacao)
                .pipe(
                    take(1)
                )
                .subscribe(
                    grupoClassificacao => resolve(grupoClassificacao),
                    (error: ApiError) => {
                        this.messageService.error(error.cause, error.message, error);
                        reject(error);
                    }
                );
        });
    }

    /** Abre a análise de mercado do cliente */
    async openAnaliseMercado(cliente: ClienteAnaliseMercadoSearchCliente) {
        try {
            this.setCurrentCliente(cliente);

            // Obtendo a perspectiva da análise de mercado selecionada
            const perspectiva = getPerspectiva(this.filter.visualizacaoPerspectiva);

            // RAP
            if (isPerspectivaRAP(perspectiva.id)) {
                this.lancamentoFormModal?.open(perspectiva, cliente.idCliente, this.filter.ano, null, null, null, true, true);
                return;
            }

            // Classificação por Grupo
            if (isPerspectivaClassificacao(perspectiva.id)) {
                const idGrupoClassificacao = this.filter.getGrupoClassificacaoSelecionado();
                if (idGrupoClassificacao > 0) { // Se possuir somente um grupo de classificação selecionado no filtro então abrimos o seu lançamento
                    // Primeiro tentando obter o lançamento da classificação do cliente, ano e grupo selecionado
                    const analiseMercadoClassificacao = await this.findAnaliseMercadoClassificacao(cliente.idCliente, this.filter.ano, idGrupoClassificacao);
                    if (analiseMercadoClassificacao) { // Se encontrar o lançamento então abrimos eles
                        this.lancamentoFormModal?.open(perspectiva, cliente.idCliente, this.filter.ano, analiseMercadoClassificacao.id, null, null, false, true);
                        return;
                    } else { // Se não, abrimos através do grupo de classificação
                        const grupoClassificacao = await this.getGrupoClassificacao(idGrupoClassificacao);
                        if (grupoClassificacao) {
                            this.lancamentoFormModal?.open(perspectiva, cliente.idCliente, this.filter.ano, null, grupoClassificacao.id, grupoClassificacao.classificacoesSlug, true, true);
                            return;
                        }
                    }
                }
            }

            // Demais
            this.formModal?.open(cliente.idCliente, this.filter.ano, perspectiva);
        } catch (error) {
            console.error(error);
        }
    }

    /** Após fechar o form modal */
    afterCloseFormModal() {
        this.searchOneCliente(this.currentCliente?.idCliente ?? null);
    }

    /** Após salvar o lançamento da análise de mercado */
    afterSaveLancamento() {
        this.searchOneCliente(this.currentCliente?.idCliente ?? null);
    }


    /** Abre o modal do cadastro do cliente */
    openCliente(cliente: ClienteAnaliseMercadoSearchCliente) {
        this.setCurrentCliente(cliente);
        this.clienteFormModal?.edit(cliente?.idCliente);
    }

    /** Após editar o cliente */
    afterEditCliente() {
        this.searchOneCliente(this.currentCliente?.idCliente ?? null);
    }

    /** Abre o modal para pré-baixar o cliente */
    preBaixarCliente(cliente: ClienteAnaliseMercadoSearchCliente) {
        this.setCurrentCliente(cliente);
        this.clienteCarteiraPreBaixarModal?.insert(cliente?.idCliente);
    }

    /** Após pré-baixar o cliente */
    afterPreBaixarCliente() {
        this.searchOneCliente(this.currentCliente?.idCliente ?? null);
    }

    /** Abre o modal para baixar o cliente */
    baixarCliente(cliente: ClienteAnaliseMercadoSearchCliente) {
        this.setCurrentCliente(cliente);
        this.clienteCarteiraBaixarModal?.insert(cliente?.idCliente, cliente?.motivoBaixaCliente);
    }

    /** Após baixar o cliente */
    afterBaixarCliente() {
        this.searchOneCliente(this.currentCliente?.idCliente ?? null);
    }

    /** Abre o modal para inserir uma compras no cliente */
    insertCentralCompra() {
        this.clienteCentralCompraFormModal?.insert(null, pessoaCentralCompras.id);
    }

    /** Após inserir uma central de compras */
    afterInsertCentralCompra(cliente: Cliente) {
        if (!cliente) {
            return;
        }

        this.searchOneCliente(cliente.id);
    }

    /** Abre o modal para atualizar a central de compras */
    editCentralCompra(cliente: ClienteAnaliseMercadoSearchCliente) {
        if (!cliente) {
            return;
        }

        this.centralCompraFormModal?.edit(cliente.idCliente);
    }

    /** Após salvar uma central de compras */
    afterSaveCentralCompra(centralCompras: ClienteCentralCompra) {
        if (!centralCompras) {
            return;
        }

        // Pesquisando a central de compras
        this.searchOneCliente(centralCompras.id);
        // Pesquisando os participantes da central de compras
        centralCompras.participantes?.forEach(participante => this.searchOneCliente(participante.id));
    }

    /** Abre o modal para inserir um participante em uma compras no cliente */
    insertParticipanteCentralCompra(cliente: ClienteAnaliseMercadoSearchCliente) {
        if (!cliente) {
            return;
        }

        const participante = new ClienteCentralCompraParticipante(cliente.idCliente, cliente.idPessoa, cliente.codigoIntegracaoErpCliente, cliente.cnpjCpfCliente, cliente.nomeCliente, cliente.nomeFantasiaCliente);
        this.centralCompraParticipanteFormModal?.insert(participante);
    }


    ngOnDestroy(): void {
        this.searchViewSubs$?.unsubscribe();
        this.searchClientesSubs$?.unsubscribe();
        this.clientes?.unsubscribe();
        this.list = null;
    }
}
